/* eslint-disable no-param-reassign */
import React from 'react';

import { createAsyncThunk } from '@reduxjs/toolkit';
import { InfoBlock } from '@picsio/ui';

import l10n from '../../shared/strings';
import {
  getLastLiveChannelsForVariantGroup,
  markProductChannelsAsDraft,
} from './helpers/channelHelpers';
import { resolveChannelLabels } from './helpers/showPublishedToChannelToast';
import { syncProductHasVariants } from './helpers/productVariantsHelpers';

const getOptionVariants = (product, optionName) => (product?.variants || []).filter(
  (variant) => Object.prototype.hasOwnProperty.call(variant.options || {}, optionName),
);

export const deleteOption = createAsyncThunk(
  'products/deleteOption',
  async ({ productId, optionName, listingVariantIds = [] }, {
    extra: { Logger, Toast, sdk },
    getState,
  }) => {
    try {
      const product = getState().products.items.find(({ _id }) => _id === productId);
      const option = product?.availableOptions?.find(({ name }) => name === optionName);

      if (!option?.name) {
        throw new Error('Option not found');
      }

      const channelsToUnpublish = getLastLiveChannelsForVariantGroup(
        product,
        getOptionVariants(product, optionName),
        listingVariantIds,
        getState().products,
      );

      if (listingVariantIds.length) {
        /** Sequential intentionally — server checks isLastLiveListingVariant per variant; parallel would race: all variants see each other as live and none triggers the listing wipe. */
        // eslint-disable-next-line no-restricted-syntax
        for (const id of listingVariantIds) {
          // eslint-disable-next-line no-await-in-loop
          await sdk.products.deleteVariant(id);
        }
      }

      const { data } = await sdk.products.removeOption(productId, option.name);
      return { data, channelsToUnpublish };
    } catch (error) {
      Toast(
        'Option is not deleted. Refresh and try again',
        { autoClose: false, type: 'error' },
      );
      Logger.error(new Error('Error option deleting'), { error }, [
        'DeleteOptionFailed',
        { errorMessage: 'Option is not deleted on the backend.' },
      ]);
      throw error;
    }
  },
  {
    condition: async ({ productId, optionName, listingVariantIds = [] }, { getState, extra: { showDialogAsync } }) => {
      const product = getState().products.items.find(({ _id }) => _id === productId);
      const channelsToUnpublish = getLastLiveChannelsForVariantGroup(
        product,
        getOptionVariants(product, optionName),
        listingVariantIds,
        getState().products,
      );

      if (!channelsToUnpublish.length) return true;

      const channelNames = resolveChannelLabels(channelsToUnpublish, getState().user?.team?.salesChannels || []);

      try {
        await showDialogAsync({
          title: l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_OPTION.TITLE_CONFIRM(optionName),
          text: l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_OPTION.TEXT_LAST_LIVE_OPTION(optionName, channelNames),
          children: <InfoBlock className="m-t-16">{l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_OPTION.LAST_LIVE_VARIANTS_NOTE}</InfoBlock>,
          textBtnCancel: l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_OPTION.BTN_CANCEL,
          textBtnOk: l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_OPTION.BTN_OK,
          buttonOkColor: 'danger',
        }).promise;
        return true;
      } catch {
        return false;
      }
    },
  },
);

export const reducer = (builder) => {
  builder.addCase(
    deleteOption.pending,
    (state, { meta }) => {
      state.items.forEach((product) => {
        if (product._id === meta.arg.productId) {
          product.isBusy = true;
        }
      });
    },
  ).addCase(
    deleteOption.fulfilled,
    (state, { payload, meta }) => {
      state.items = state.items.map((product) => {
        if (product._id === meta.arg.productId) {
          const updatedProduct = {
            ...product,
            isBusy: false,
            variants: payload.data.variants,
            availableOptions: payload.data.availableOptions ?? (product.availableOptions || []).filter(
              (opt) => opt.name !== meta.arg.optionName,
            ),
            hasVariants: payload.data.hasVariants ?? product.hasVariants,
          };
          if (payload.data.hasVariants == null) {
            syncProductHasVariants(updatedProduct);
          }
          markProductChannelsAsDraft(updatedProduct, payload.channelsToUnpublish, state);
          return updatedProduct;
        }
        return product;
      });
    },
  ).addCase(
    deleteOption.rejected,
    (state, { meta, error }) => {
      state.items.forEach((product) => {
        if (product._id === meta.arg.productId) {
          product.isBusy = false;
        }
      });
      state.error = error;
    },
  );
};
